import { useCustomMutation } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { useCallback } from 'react';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';

/**
 * E34 · the practice's pathway commands from the portal — DefinePathway, RevisePathway, RetirePathway
 * (the library and the Builder), EnrollInPathway (the hub's drawer), ReleasePathwayStep and
 * WithdrawFromPathway (the Pathway progress page) — each ONE deliberate act (a button), posted with
 * `errorNotification: false` so the screen reports the outcome itself (portal conventions): a CODED
 * refusal (`homework.errors.PATHWAY_*`, already in the therapist's language) is shown verbatim, against
 * the thing it refused; anything else is the screen's own copy plus Sentry. Nothing retries or autosaves.
 */
export type PathwayOperation =
  | 'definePathway'
  | 'revisePathway'
  | 'retirePathway'
  | 'enrollInPathway'
  | 'releasePathwayStep'
  | 'withdrawFromPathway'
  // block 5a — the backoffice's course acts (members only)
  | 'setPathwayAccessRule'
  | 'setPracticeCapability'
  | 'reviewPathwayPublication';

const INPUT_TYPE: Record<PathwayOperation, string> = {
  definePathway: 'DefinePathwayInput!',
  revisePathway: 'RevisePathwayInput!',
  retirePathway: 'RetirePathwayInput!',
  enrollInPathway: 'EnrollInPathwayInput!',
  releasePathwayStep: 'ReleasePathwayStepInput!',
  withdrawFromPathway: 'WithdrawFromPathwayInput!',
  setPathwayAccessRule: 'SetPathwayAccessRuleInput!',
  setPracticeCapability: 'SetPracticeCapabilityInput!',
  reviewPathwayPublication: 'ReviewPathwayPublicationInput!',
};

const PAYLOAD_FIELDS: Record<PathwayOperation, string[]> = {
  definePathway: ['pathwayId', 'version', 'recorded'],
  revisePathway: ['pathwayId', 'version', 'recorded'],
  retirePathway: ['pathwayId', 'version', 'recorded'],
  enrollInPathway: ['enrollmentId', 'recorded'],
  releasePathwayStep: ['enrollmentId', 'assignmentId', 'recorded'],
  withdrawFromPathway: ['enrollmentId', 'recorded'],
  setPathwayAccessRule: ['pathwayId', 'recorded'],
  setPracticeCapability: ['practiceId', 'recorded'],
  reviewPathwayPublication: ['pathwayId', 'version', 'recorded'],
};

export type PathwayCommandResult =
  | { ok: true; data: Record<string, unknown> }
  | { ok: false; code: string | undefined; message: string };

export const usePathwayCommand = () => {
  const { mutateAsync, isLoading } = useCustomMutation<Record<string, unknown>>();

  const run = useCallback(
    async (
      operation: PathwayOperation,
      input: Record<string, unknown>,
      fallback: string,
    ): Promise<PathwayCommandResult> => {
      try {
        const res = await mutateAsync({
          url: '',
          method: 'post',
          values: {},
          errorNotification: false,
          successNotification: false,
          meta: {
            operation,
            fields: PAYLOAD_FIELDS[operation],
            variables: {
              input: { name: 'input', type: INPUT_TYPE[operation], value: input },
            },
          },
        });
        const data = res?.data;
        if (!data) return { ok: false, code: undefined, message: fallback };
        return { ok: true, data };
      } catch (error) {
        const info = graphqlErrorInfo(error);
        if (info.code && info.message) {
          return { ok: false, code: info.code, message: info.message };
        }
        Sentry.captureException(error);
        return { ok: false, code: info.code, message: fallback };
      }
    },
    [mutateAsync],
  );

  return { run, isLoading };
};
